/**
 * Sidebar navigation — KeyCrew brand: deep navy background, single blue accent.
 * Uses inline SVG icons; no emoji.
 */
import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import contentStudioService from '../../services/contentStudioService';

/* ── Icons ────────────────────────────────────────────────────────────────── */
const icons = {
  search: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  interviews: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
  articles: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  ),
  sources: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  listings: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  ),
  reports: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  ),
  pressReleases: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z" />
    </svg>
  ),
  podcasts: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
    </svg>
  ),
  collapse: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      {/* panel-left-close: rectangle split into sidebar + content, with inward arrow */}
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M9 3H5a2 2 0 00-2 2v14a2 2 0 002 2h4M9 3h10a2 2 0 012 2v14a2 2 0 01-2 2H9M9 3v18M13 9l-2 3 2 3" />
    </svg>
  ),
  clients: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0H5m14 0h2M5 21H3m4-14h2m-2 4h2m4-4h2m-2 4h2" />
    </svg>
  ),
  menu: (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  ),
  settings: (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

const deepQueryIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const storyClustersIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M7 8h10M7 12h6m-6 4h8M5 3h14a2 2 0 012 2v14a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2z" />
  </svg>
);

const adminIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const permissionsIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </svg>
);

const productivityIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const activityIcon = (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);

// Standalone global utility (kept above the grouped sections).
// `section` maps to backend sections: search spans editorial content.
const TOP_ITEMS = [
  { path: '/search', name: 'Search', icon: icons.search, section: 'editorial', requires: 'interviews.view' },
];

// Three grouped sections, in the Content Studio spec order.
// Each group carries its access-control section; groups the current user's
// role can't view are hidden entirely.
const NAV_GROUPS = [
  {
    label: 'Content Studio',
    section: 'cs',
    items: [
      // "Partners" — label only. The route and the underlying `content_studio_clients` collection
      // are unchanged.
      { path: '/content-studio/clients',     name: 'Partners',   icon: icons.clients },
      // MOCKUP: hard-coded sample data, no collection and no endpoint behind it. A Content Studio
      // source is a person belonging to a partner (REALM has ~600; most partners have one).
      { path: '/content-studio/sources',     name: 'Sources',    icon: icons.sources },
      { path: '/content-studio/submissions', name: 'Interviews', icon: icons.interviews },
      // Own route, own team-scoped view — mirrors the Editorial group's Listings link, which
      // points at /listings instead. Each shows only that team's listings.
      { path: '/content-studio/listings',    name: 'Listings',   icon: icons.listings, beta: true },
      { path: '/content-studio/articles',    name: 'Articles',   icon: icons.articles },
      { path: '/content-studio/distribution', name: 'Placements', icon: icons.reports, children: [
        { lens: 'manual_action', name: 'Manual Action', hint: 'Outlets that need a person to upload by hand (manual + PRNow), not yet submitted. Mark them submitted once done.' },
        { lens: 'submitted_waiting', name: 'Submitted / Waiting', hint: 'Out the door, awaiting go-live — all RSS placements + manual/PRNow once submitted. Paste the published URL here to mark it live.' },
        { lens: 'published', name: 'Published', hint: 'Live placements — each with a URL is a Win. Push to the client’s Wins report from here.' },
      ] },
      { path: '/content-studio/settings', name: 'Settings', icon: icons.settings, requires: 'cs.manage' },
    ],
  },
  {
    label: 'Editorial',
    section: 'editorial',
    items: [
      // Sources → Interviews → Articles, mirroring Content Studio's
      // Clients → Interviews → Articles: the party, then what they gave us, then what we made.
      // Sources is granted by `interviews.view` because it is the person side of an interview,
      // so the read-only Journalist role that exists to browse interviews must see it too.
      { path: '/sources',    name: 'Sources',    icon: icons.sources, requires: 'interviews.view' },
      { path: '/interviews', name: 'Interviews', icon: icons.interviews, requires: 'interviews.view' },
      { path: '/listings',   name: 'Listings',   icon: icons.listings, requires: 'interviews.view', beta: true },
      { path: '/articles',   name: 'Articles',   icon: icons.articles },
      { path: '/settings',   name: 'Settings',   icon: icons.settings, requires: 'editorial.manage' },
    ],
  },
  {
    // "Intelligence" — analysis tools that read across the whole archive rather than acting on
    // one record. Renamed from "Coming Soon" once Deep Query and Story Clusters were both live:
    // a shipped feature parked under "Coming Soon" reads as unfinished and stops people opening
    // it. Both carry a Beta pill instead, which says "new, expect rough edges" without saying
    // "not ready".
    label: 'Intelligence',
    section: 'editorial',
    items: [
      // Hidden from the nav, not removed. The routes in ViewRouter and the backend routers are
      // untouched, so /podcasts, /press-releases and /reports still work if you go to them
      // directly — uncomment a line here to put it back in the sidebar.
      // { path: '/podcasts',        name: 'Podcasts',       icon: icons.podcasts },
      // { path: '/press-releases',  name: 'Press Releases', icon: icons.pressReleases },
      // { path: '/reports',         name: 'Data Reports',   icon: icons.reports },
      { path: '/deep-query',      name: 'Deep Query',     icon: deepQueryIcon, beta: true },
      { path: '/story-clusters',  name: 'Story Clusters', icon: storyClustersIcon, beta: true },
    ],
  },
  {
    label: 'Admin',
    section: 'admin',
    items: [
      { path: '/admin/users',    name: 'Users',        icon: adminIcon },
      { path: '/admin/permissions', name: 'Permissions', icon: permissionsIcon },
      { path: '/admin/api-keys', name: 'API Keys',     icon: adminIcon },
      { path: '/admin/activity', name: 'Activity Log', icon: activityIcon },
      { path: '/admin/productivity', name: 'Productivity', icon: productivityIcon },
    ],
  },
];

/* ── Shared link class builders ───────────────────────────────────────────── */
const linkClass = ({ isActive }) =>
  `w-full flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors ${
    isActive
      ? 'bg-white/10 text-white border-l-2 border-[#4A6CF7] pl-[10px]'
      : 'text-slate-400 hover:bg-white/5 hover:text-white border-l-2 border-transparent pl-[10px]'
  }`;

const collapsedLinkClass = ({ isActive }) =>
  `w-full flex justify-center py-2.5 px-2 rounded-md transition-colors ${
    isActive
      ? 'bg-white/10 text-white'
      : 'text-slate-400 hover:bg-white/5 hover:text-white'
  }`;

/* ── Component ────────────────────────────────────────────────────────────── */
const Sidebar = () => {
  const { state, toggleSidebar } = useAppContext();
  const { logout, user, can, isSuperAdmin } = useAuth();
  const { ui } = state;
  const location = useLocation();
  const [placementsOpen, setPlacementsOpen] = useState(true);

  // Role-based visibility. Each item needs its `requires` permission, or the
  // group section's view permission by default; a group shows when it has at
  // least one visible item (so a Journalist sees Editorial → Interviews only).
  const sectionVisible = (section) =>
    section === 'admin' ? isSuperAdmin : can(`${section}.view`);
  const itemVisible = (section) => (item) =>
    item.requires ? can(item.requires) : sectionVisible(section);
  const visibleTop = TOP_ITEMS.filter((i) => itemVisible(i.section)(i));
  const visibleGroups = NAV_GROUPS
    .map((g) => ({ ...g, items: g.items.filter(itemVisible(g.section)) }))
    .filter((g) => g.items.length > 0);
  const allItems = [...visibleTop, ...visibleGroups.flatMap((g) => g.items)];

  // Ambient workload badges. Poll so they stay roughly current as work moves.
  const [counts, setCounts] = useState({ articles: 0, manual: 0 });
  const [lensCounts, setLensCounts] = useState({});   // per-lens placement counts for the Placements submenu
  useEffect(() => {
    // Badges live on Content Studio items — skip polling for roles that can't see them
    if (!sectionVisible('cs')) return undefined;
    let alive = true;
    const fetchCounts = async () => {
      try { const c = await contentStudioService.getNavCounts(); if (alive) setCounts(c || { articles: 0, manual: 0 }); }
      catch { /* badges are non-critical — stay silent on failure */ }
      try { const s = await contentStudioService.placementStats(); if (alive) setLensCounts(s?.by_lens || {}); }
      catch { /* non-critical */ }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 30000);
    const onFocus = () => fetchCounts();
    window.addEventListener('focus', onFocus);
    return () => { alive = false; clearInterval(id); window.removeEventListener('focus', onFocus); };
  }, [user]);

  // Which nav paths get a number badge, and from which count.
  const badgeFor = (path) => {
    if (path === '/content-studio/articles') return counts.articles || 0;
    if (path === '/content-studio/manual-submissions') return counts.manual || 0;
    return 0;
  };

  const handleLogout = () => {
    if (window.confirm('Are you sure you want to logout?')) logout();
  };

  if (ui.sidebarCollapsed) {
    return (
      <div className="w-14 bg-kc-navy flex flex-col border-r border-white/5">
        <div className="h-14 flex items-center justify-center border-b border-white/10">
          <button onClick={toggleSidebar} className="text-slate-400 hover:text-white p-1 transition-colors">
            {icons.menu}
          </button>
        </div>
        <nav className="flex-1 py-3 px-1 space-y-0.5 overflow-y-auto">
          {allItems.map((item) => {
            const badge = badgeFor(item.path);
            return (
              <NavLink key={item.path} to={item.path} className={collapsedLinkClass}
                title={badge > 0 ? `${item.name} (${badge})` : item.name}>
                <span className="relative">
                  {item.icon}
                  {badge > 0 && (
                    <span className="absolute -top-1.5 -right-2 inline-flex items-center justify-center min-w-[1rem] h-4 px-1 text-[9px] font-bold rounded-full bg-[#4A6CF7] text-white">{badge}</span>
                  )}
                </span>
              </NavLink>
            );
          })}
        </nav>
      </div>
    );
  }

  return (
    <div className="w-56 bg-kc-navy flex flex-col border-r border-white/5">
      {/* Logo */}
      <div className="h-14 flex items-center justify-between px-4 border-b border-white/10">
        <div className="flex items-center gap-1">
          <img
            src="https://customer-assets.emergentagent.com/job_keycrew-cms/artifacts/tvb3htyd_2.png"
            alt="KeyCrew"
            className="h-7 w-auto"
          />
          <span className="text-white text-xl font-bold">!</span>
        </div>
        <button onClick={toggleSidebar} className="text-slate-400 hover:text-white p-1 transition-colors">
          {icons.collapse}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 px-2 overflow-y-auto">
        {/* Standalone utility */}
        <div className="space-y-0.5">
          {visibleTop.map((item) => (
            <NavLink key={item.path} to={item.path} className={linkClass}>
              {item.icon}
              <span>{item.name}</span>
            </NavLink>
          ))}
        </div>

        {/* Grouped sections */}
        {visibleGroups.map((group) => (
          <div key={group.label} className="mt-5">
            <p className="px-3 mb-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500">
              {group.label}
            </p>
            <div className="space-y-0.5">
              {group.items.map((item) => {
                // Collapsible parent (Placements) with lifecycle-lens children.
                if (item.children) {
                  const onPage = location.pathname === item.path;
                  const curLens = new URLSearchParams(location.search).get('lens') || 'all';
                  return (
                    <div key={item.path}>
                      <div className={`w-full flex items-center gap-3 py-2.5 px-3 text-sm font-medium rounded-md transition-colors ${onPage ? 'text-white' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                        <NavLink to={item.path} className="flex items-center gap-3 flex-1 min-w-0">
                          {item.icon}
                          <span>{item.name}</span>
                        </NavLink>
                        <button onClick={() => setPlacementsOpen((o) => !o)} className="text-slate-400 hover:text-white flex-shrink-0" aria-label="Toggle submenu">
                          <svg className={`w-3 h-3 transition-transform ${placementsOpen ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        </button>
                      </div>
                      {placementsOpen && (
                        <div className="ml-6 mt-0.5 space-y-0.5 border-l border-white/10 pl-2">
                          {item.children.map((c) => {
                            const active = onPage && curLens === c.lens;
                            return (
                              <NavLink key={c.lens} to={`${item.path}?lens=${c.lens}`}
                                data-testid={`nav-lens-${c.lens}`} title={c.hint}
                                className={`flex items-center gap-2 py-1.5 px-2 text-[13px] rounded-md transition-colors ${active ? 'text-white bg-white/10' : 'text-slate-400 hover:bg-white/5 hover:text-white'}`}>
                                <span>{c.name}</span>
                                {c.lens !== 'published' && lensCounts[c.lens] > 0 && (
                                  <span data-testid={`nav-lens-count-${c.lens}`}
                                    className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold rounded-full bg-[#4A6CF7] text-white">{lensCounts[c.lens]}</span>
                                )}
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                }
                const badge = badgeFor(item.path);
                return (
                  <NavLink key={item.path} to={item.path} className={linkClass}>
                    {item.icon}
                    <span>{item.name}</span>
                    {item.beta && (
                      <span className="ml-auto inline-flex items-center h-4 px-1.5 text-[9px] font-semibold uppercase tracking-wide rounded-full bg-white/10 text-slate-300">
                        Beta
                      </span>
                    )}
                    {badge > 0 && (
                      <span data-testid={`nav-badge-${item.path}`}
                        className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-bold rounded-full bg-[#4A6CF7] text-white">{badge}</span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="px-2 py-3 border-t border-white/10">
        {user && (
          <div className="px-3 pb-2">
            <p className="text-xs font-medium text-slate-300 truncate">{user.name}</p>
            <p className="text-[10px] text-slate-500 truncate">{user.role_label || user.role}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          data-testid="sidebar-logout"
          className="w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium text-slate-400 hover:bg-white/5 hover:text-white transition-colors"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.75} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>Logout</span>
        </button>
        <p className="px-3 mt-2 text-[10px] text-slate-600 uppercase tracking-widest">KeyCrew CMS v2.0</p>
      </div>
    </div>
  );
};

export default Sidebar;
